import React, { useEffect, useMemo, useState } from 'react'
import { useUnit } from 'effector-react'
import dayjs from 'dayjs'
import { useProcessingList } from './hooks'
import { CCardBody } from '@coreui/react-pro'
import {
  Card,
  Divider,
  Flex,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { ProcessingTransaction, TotalKPI } from './types'
import { ProcessingFilters } from '../atoms/ProcessingFilters'
import { $userProfile } from 'src/features/user'
import { rolesObj } from 'src/libs/role'
import { RangePickerProps } from 'antd/es/date-picker'
import {
  CheckCircleOutlined,
  SwapOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { getPercentageStatus } from '../../shared/percentageStatus'

const { Text } = Typography

const formatNum = (val?: number) =>
  val != null ? val.toLocaleString('ru-RU') : '—'

const numCell = (val: number) => (
  <span style={{ fontFamily: 'monospace' }}>{formatNum(val)}</span>
)

const pctTag = (val: number) => {
  if (val == null) return <Text type="secondary">—</Text>
  return (
    <Tag
      color={getPercentageStatus(val)}
      style={{ fontFamily: 'monospace', minWidth: 58, textAlign: 'center' }}
    >
      {val.toFixed(2)}%
    </Tag>
  )
}

const speedPctTag = (val: number) => {
  if (val == null) return <Text type="secondary">—</Text>
  return (
    <Tag
      color={getPercentageStatus(val)}
      style={{ fontFamily: 'monospace', minWidth: 58, textAlign: 'center' }}
    >
      {val.toFixed(2)}%
    </Tag>
  )
}

const groupHeader = (label: string, color: string) => (
  <span
    style={{
      textAlign: 'center',
      color,
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}
  >
    {label}
  </span>
)

const pctTitle = (
  <Tooltip title="Процент выполнения относительно ожидаемого объёма">%</Tooltip>
)

const SummaryCards: React.FC<{ totalKPI: TotalKPI | null }> = ({
  totalKPI,
}) => {
  if (!totalKPI) return null

  const depTotal =
    totalKPI.totalDepositApprove + totalKPI.totalDepositRejected
  const depRejPct = depTotal
    ? ((totalKPI.totalDepositRejected / depTotal) * 100).toFixed(1)
    : '0'
  const wdTotal =
    totalKPI.totalWithdrawalApprove + totalKPI.totalWithdrawalRejected
  const wdRejPct = wdTotal
    ? ((totalKPI.totalWithdrawalRejected / wdTotal) * 100).toFixed(1)
    : '0'
  const intTotal =
    totalKPI.totalInternalApprove + totalKPI.totalInternalRejected
  const intRejPct = intTotal
    ? ((totalKPI.totalInternalRejected / intTotal) * 100).toFixed(1)
    : '0'
  const totalProcessed = totalKPI.totalAllApprove + totalKPI.totalAllRejected
  const rejPct = totalProcessed
    ? ((totalKPI.totalAllRejected / totalProcessed) * 100).toFixed(1)
    : '0'

  return (
    <Flex gap={12} style={{ marginBottom: 16 }} wrap="wrap">
      <Card size="small" style={{ flex: 1, minWidth: 160 }}>
        <Statistic
          title={
            <>
              <CheckCircleOutlined style={{ color: '#60a5fa' }} /> Deposit
            </>
          }
          value={depTotal.toLocaleString('ru-RU')}
          valueStyle={{ fontSize: 20, fontFamily: 'monospace' }}
          suffix={
            <Tag color={Number(depRejPct) <= 5 ? 'success' : 'warning'} style={{ fontSize: 10 }}>
              {depRejPct}% rejected
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 160 }}>
        <Statistic
          title={
            <>
              <SwapOutlined style={{ color: '#c084fc' }} /> Withdrawal
            </>
          }
          value={wdTotal.toLocaleString('ru-RU')}
          valueStyle={{ fontSize: 20, fontFamily: 'monospace' }}
          suffix={
            <Tag color={Number(wdRejPct) <= 10 ? 'success' : 'warning'} style={{ fontSize: 10 }}>
              {wdRejPct}% rejected
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 160 }}>
        <Statistic
          title={
            <>
              <SwapOutlined style={{ color: '#22d3ee' }} /> Internal
            </>
          }
          value={intTotal.toLocaleString('ru-RU')}
          valueStyle={{ fontSize: 20, fontFamily: 'monospace' }}
          suffix={
            <Tag color={Number(intRejPct) <= 10 ? 'success' : 'warning'} style={{ fontSize: 10 }}>
              {intRejPct}% rejected
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 160 }}>
        <Statistic
          title={
            <>
              <TeamOutlined style={{ color: '#4ade80' }} /> Total
            </>
          }
          value={totalProcessed.toLocaleString('ru-RU')}
          valueStyle={{ fontSize: 20, fontFamily: 'monospace' }}
          suffix={
            <Tag color={Number(rejPct) <= 10 ? 'success' : 'warning'} style={{ fontSize: 10 }}>
              {rejPct}% rejected
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 160 }}>
        <Statistic
          title={
            <>
              <ThunderboltOutlined style={{ color: '#fbbf24' }} /> Speed
            </>
          }
          value={totalKPI.totalQuickRecordsQuantity}
          valueStyle={{ fontSize: 20, fontFamily: 'monospace' }}
          suffix={
            <Tag
              color={getPercentageStatus(totalKPI.totalQuickRecordsPercent)}
              style={{ fontSize: 10 }}
            >
              {totalKPI.totalQuickRecordsPercent}%
            </Tag>
          }
        />
      </Card>
    </Flex>
  )
}

export const ProcessingTransactionsList: React.FC = () => {
  const { kpiList, loading, onChangeProcessingList, totalCountOfKPI, totalKPI } =
    useProcessingList()

  const userProfile = useUnit($userProfile)
  const hasFullAccess = useMemo(
    () =>
      userProfile.roles.includes(rolesObj.ceo) ||
      userProfile.roles.includes(rolesObj.cfo),
    [userProfile],
  )

  const [filterUserIds, setFilterUserIds] = useState<string[] | undefined>()
  const [datePeriod, setDatePeriod] = useState<RangePickerProps['value']>(() => [
    dayjs().startOf('month'),
    dayjs(),
  ])
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    }
  }

  useEffect(() => {
    if (datePeriod?.[0] && datePeriod?.[1]) {
      const userIds = hasFullAccess ? filterUserIds : [userProfile.id]
      onChangeProcessingList({
        userIds,
        beginPeriodAt: datePeriod[0].format('YYYY-MM-DD'),
        endPeriodAt: datePeriod[1].format('YYYY-MM-DD'),
        limit: pageSize,
        page,
      })
    }
  }, [hasFullAccess, userProfile.id, filterUserIds, datePeriod, pageSize, page])

  const handleFiltersChange = ({
    userIds,
    datePeriod: nextDatePeriod,
  }: {
    userIds?: string[]
    datePeriod?: RangePickerProps['value']
  }) => {
    setDatePeriod(nextDatePeriod)
    setFilterUserIds(userIds)
    setPage(1)
  }

  const columns: ColumnsType<ProcessingTransaction> = [
    {
      title: 'Manager',
      dataIndex: 'username',
      fixed: 'left',
      width: 130,
      sorter: (a, b) => a.username.localeCompare(b.username),
      defaultSortOrder: 'ascend',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: groupHeader('Deposit', '#60a5fa'),
      children: [
        {
          title: 'Approved',
          dataIndex: 'depositApprove',
          align: 'right',
          width: 90,
          sorter: (a, b) => a.depositApprove - b.depositApprove,
          render: numCell,
        },
        {
          title: 'Rejected',
          dataIndex: 'depositRejected',
          align: 'right',
          width: 85,
          sorter: (a, b) => a.depositRejected - b.depositRejected,
          render: numCell,
        },
        {
          title: pctTitle,
          dataIndex: 'depositExecPercent',
          align: 'center',
          width: 80,
          sorter: (a, b) => a.depositExecPercent - b.depositExecPercent,
          render: (value: number, record: ProcessingTransaction) => {
            const total = record.depositApprove + record.depositRejected
            if (total > 0 && record.depositExcludedQuantity === total) {
              return (
                <Tooltip title="Все депозиты менеджера за выбранный период исключены из расчёта KPI">
                  <Text type="secondary">—</Text>
                </Tooltip>
              )
            }
            return pctTag(value)
          },
        },
      ],
    },
    {
      title: groupHeader('Withdrawal', '#c084fc'),
      children: [
        {
          title: 'Approved',
          dataIndex: 'withdrawalApprove',
          align: 'right',
          width: 90,
          sorter: (a, b) => a.withdrawalApprove - b.withdrawalApprove,
          render: numCell,
        },
        {
          title: 'Rejected',
          dataIndex: 'withdrawalRejected',
          align: 'right',
          width: 85,
          sorter: (a, b) => a.withdrawalRejected - b.withdrawalRejected,
          render: numCell,
        },
        {
          title: pctTitle,
          dataIndex: 'withdrawalExecPercent',
          align: 'center',
          width: 80,
          sorter: (a, b) => a.withdrawalExecPercent - b.withdrawalExecPercent,
          render: pctTag,
        },
      ],
    },
    {
      title: groupHeader('Internal', '#22d3ee'),
      children: [
        {
          title: 'Approved',
          dataIndex: 'internalApprove',
          align: 'right',
          width: 90,
          sorter: (a, b) => a.internalApprove - b.internalApprove,
          render: numCell,
        },
        {
          title: 'Rejected',
          dataIndex: 'internalRejected',
          align: 'right',
          width: 85,
          sorter: (a, b) => a.internalRejected - b.internalRejected,
          render: numCell,
        },
        {
          title: pctTitle,
          dataIndex: 'internalExecPercent',
          align: 'center',
          width: 80,
          sorter: (a, b) => a.internalExecPercent - b.internalExecPercent,
          render: pctTag,
        },
      ],
    },
    {
      title: groupHeader('All', '#4ade80'),
      children: [
        {
          title: 'Approved',
          dataIndex: 'allApprove',
          align: 'right',
          width: 90,
          sorter: (a, b) => a.allApprove - b.allApprove,
          render: numCell,
        },
        {
          title: 'Rejected',
          dataIndex: 'allRejected',
          align: 'right',
          width: 85,
          sorter: (a, b) => a.allRejected - b.allRejected,
          render: numCell,
        },
        {
          title: pctTitle,
          dataIndex: 'allExecPercent',
          align: 'center',
          width: 80,
          sorter: (a, b) => a.allExecPercent - b.allExecPercent,
          render: pctTag,
        },
      ],
    },
    {
      title: (
        <Tooltip title="Скоростные заявки — обработанные менее чем за 15 минут">
          {groupHeader('Speed', '#fbbf24')}
        </Tooltip>
      ),
      children: [
        {
          title: (
            <Tooltip title="Количество скоростных заявок">Number</Tooltip>
          ),
          dataIndex: 'quickRecordsQuantity',
          align: 'right',
          width: 90,
          sorter: (a, b) => a.quickRecordsQuantity - b.quickRecordsQuantity,
          render: numCell,
        },
        {
          title: (
            <Tooltip title="Процент скоростных заявок от общего числа">
              %
            </Tooltip>
          ),
          dataIndex: 'quickRecordsPercent',
          align: 'center',
          width: 80,
          sorter: (a, b) => a.quickRecordsPercent - b.quickRecordsPercent,
          render: speedPctTag,
        },
      ],
    },
  ]

  const renderSummary = () => {
    if (!totalKPI) return null
    return (
      <Table.Summary.Row style={{ fontWeight: 600 }}>
        <Table.Summary.Cell index={0}>
          <strong>Total</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <strong>{formatNum(totalKPI.totalDepositApprove)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          <strong>{formatNum(totalKPI.totalDepositRejected)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="center">
          <strong>
            {formatNum(
              totalKPI.totalDepositApprove + totalKPI.totalDepositRejected,
            )}
          </strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <strong>{formatNum(totalKPI.totalWithdrawalApprove)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          <strong>{formatNum(totalKPI.totalWithdrawalRejected)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="center">
          <strong>
            {formatNum(
              totalKPI.totalWithdrawalApprove +
                totalKPI.totalWithdrawalRejected,
            )}
          </strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">
          <strong>{formatNum(totalKPI.totalInternalApprove)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={8} align="right">
          <strong>{formatNum(totalKPI.totalInternalRejected)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={9} align="center">
          <strong>
            {formatNum(
              totalKPI.totalInternalApprove + totalKPI.totalInternalRejected,
            )}
          </strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={10} align="right">
          <strong>{formatNum(totalKPI.totalAllApprove)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={11} align="right">
          <strong>{formatNum(totalKPI.totalAllRejected)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={12} align="center">
          <strong>
            {formatNum(totalKPI.totalAllApprove + totalKPI.totalAllRejected)}
          </strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={13} align="right">
          <strong>{formatNum(totalKPI.totalQuickRecordsQuantity)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={14} align="center">
          <strong>{totalKPI.totalQuickRecordsPercent}%</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    )
  }

  return (
    <CCardBody>
      <ProcessingFilters
        classname="mb-3"
        value={{ userIds: filterUserIds, datePeriod }}
        onChange={handleFiltersChange}
        hasFullAccess={hasFullAccess}
      />

      <SummaryCards totalKPI={totalKPI} />

      <Table
        dataSource={kpiList}
        bordered
        size="small"
        scroll={{ x: 1400 }}
        loading={loading}
        rowKey="userId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          pageSize: pageSize,
          total: totalCountOfKPI,
        }}
        summary={renderSummary}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      />

      <Flex gap={16} style={{ marginTop: 12 }} align="center">
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}
        >
          Процент выполнения относительно ожидаемого объёма:
        </Text>
        <Tag color="error">&lt; 70%</Tag>
        <Tag color="warning">70–85.99%</Tag>
        <Tag color="success">≥ 86%</Tag>
        <Divider type="vertical" />
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}
        >
          Speed (&lt;15 min):
        </Text>
        <Tag color="error">&lt; 70%</Tag>
        <Tag color="warning">70–85.99%</Tag>
        <Tag color="success">≥ 86%</Tag>
      </Flex>
    </CCardBody>
  )
}
